import isEmpty from 'lodash/isEmpty';
import { createSelector } from 'reselect';
import moment from 'moment-timezone';
import {
  extractSelectedTicketBusinessDetailsFromState,
  extractSelectedTicketFromState,
  extractSelectedWizardMacro,
  getSearchTypeOfSelectedAction,
  getSelectedSearchResult,
  getSelectedSearchResultIndex,
  getSelectedSearchResultType,
  getUser,
  isSpecialist,
  getSubscriptionSearchParams,
  isSelectedTicketTaken,
  getDecisionNodeAtPath,
  getSelectedRbiItemsForRefund,
  getRefundAmountDetails,
  getRbiCouponsInfo,
  getRbiAssignedOffers,
  extractCurrentDecisionTreePath,
  getRbiApprovedRefundAmount,
  getSimplrAccountIdOfSelectedTicket,
} from '../extractFromState';
import { isCouponIssuedBySupport } from '../util/rbiUtil';
import * as searchActions from '../constants/searchActions';
import { SEARCH_CATEGORIES, SEARCH_TYPES } from 'constants/dynamicIntegrations';
import { TICKET_STATUS_ASSIGNED } from '../constants/ticketStatuses';
import { ADMIN, EXPERT_AND_ADMIN } from '../constants/historicalTicketSearchStatuses';
import get from 'lodash/get';
import * as searchTypes from '../constants/searchTypes';
import * as conversationTypes from '../constants/conversationTypes';
import * as orderSearchOptions from '../constants/searchActions';
import * as returnsSearchOptions from '../constants/returnSearchOptions';
import {
  chooseOrderingSystemForSelectedAction,
  getReadableStatusFromShippoStatus,
  getReadableStatusFromShopifyStatus,
  getMappedValueFromOrderOrItem,
  itemIsUPSSurepost,
  getCarrierCodeOfShipment,
  getTrackingNumberOfShipment,
  getItemId,
  formatOrderDateToLocalTime,
  normalizeSnakeCase,
  formatCentsToDollars,
  priceFormat,
  getMappedOrderingSystemPropertyName,
} from '../util';
import { DECISION_NODE_ACTIONS } from 'constants/decisionTree';
import { _createSelector } from '../util/selectorUtils';
import { DEFAULT_CURRENCY_CODE } from 'constants/currencyCodes';
import { CUSTOMER_EMAIL_SEARCH_INPUT } from 'constants/dynamicIntegrations';

/**
 * state.tools.general
 */

export const getGeneral = (state) => state.tools.general;

export const selectSelectedToolDomains = createSelector([getGeneral], (general) => general.selectedToolDomains || {});

/**
 * Returns the data collected by loadDynamicIntegrations
 * Helps the UI determine which search options to display
 *
 * @param {object} state
 * @returns {object}
 */
export const getDynamicIntegrationOptionsOfSelectedTicket = (state) => {
  const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);

  if (!simplrAccountId) {
    return {};
  }

  return state?.tools?.general?.integrationOptions?.[simplrAccountId] ?? {};
};

export const getDynamicIntegrationOrderSearchOptions = createSelector(
  [getDynamicIntegrationOptionsOfSelectedTicket],
  (partnerOptionsData) => partnerOptionsData?.[SEARCH_CATEGORIES.ORDER]?.[SEARCH_TYPES.ORDER.GET_ORDERS] ?? []
);

export const getDefaultCOIOrderSearchInput = createSelector(
  [getDynamicIntegrationOrderSearchOptions],
  (configuredSearchOptions) => {
    if (isEmpty(configuredSearchOptions)) {
      return orderSearchOptions.COMMON_ORDER_EMAIL;
    }

    /**
     * There can be multiple dynamic input options - select the first one
     * We intentionally order these options within the array
     */
    return orderSearchOptions.COMMON_ORDER_DYNAMIC_INPUT_0;
  }
);

export const selectCurrentShopifyConfigs = createSelector(
  [selectSelectedToolDomains],
  (selectedToolDomains) => selectedToolDomains['shopifyConfigs'] || {}
);

export const getShopifyShopNamesForSelectedTicket = createSelector(
  [selectCurrentShopifyConfigs, extractSelectedTicketFromState],
  (shopifyConfigs, ticket) => {
    if (!ticket) {
      return [];
    }
    const originalShopNames = Object.keys(shopifyConfigs);
    if (originalShopNames.length <= 1) {
      return originalShopNames;
    }

    // Multiple shops:
    // If no brand(s) specified, add that store to selectedShops.
    // If brands are specified, add ALL stores that support the ticket's brand to selectedShops.
    const selectedShops = originalShopNames.filter((originalShopName) => {
      const shopConfig = shopifyConfigs[originalShopName];
      if (!shopConfig) {
        return false;
      }

      return (
        isEmpty(shopConfig.brands) || (Array.isArray(shopConfig.brands) && shopConfig.brands.includes(ticket.brand))
      );
    });

    // If no selectedShops, return all stores.
    return !selectedShops.length ? originalShopNames : selectedShops;
  }
);

/**
 * state.ui.tools
 */

export const getShowTools = (state) => state.ui.tools.general.showTools;

/**
 * state.tools.ordersSearch
 */

export const getOrdersSearchProperties = (state) => state.tools.ordersSearch || {};

export const getSelectedMultiDomain = createSelector(
  [getOrdersSearchProperties],
  (ordersSearch) => ordersSearch.selectedMultiDomain || null
);

export const getSelectedSimplrOrderMultiDomains = createSelector(
  [getOrdersSearchProperties],
  (ordersSearch) => ordersSearch.selectedSimplrOrderDomains || []
);

export const getLoadingOrderId = (state) => state.tools.ordersSearch.loadingOrderId;

export const getSelectedOrder = createSelector(
  [getOrdersSearchProperties],
  (ordersSearch) => ordersSearch.selectedOrder || {}
);

export const getSelectedShop = createSelector([getOrdersSearchProperties], (ordersSearch) => {
  switch (ordersSearch.orderingSystem) {
    case 'shopify':
    case 'revolve':
      return ordersSearch.selectedOrder && ordersSearch.selectedOrder.shopName;
    case 'common':
      return ordersSearch?.selectedOrder?.simplrParams?.domain;
    default:
      return ordersSearch.selectedShop;
  }
});

export const getSelectedSearchOrdersShops = createSelector(
  [getOrdersSearchProperties],
  (ordersSearch) => ordersSearch.searchOrderShops || null
);

export const getSelectedShopifyConfig = createSelector(
  [selectSelectedToolDomains, getSelectedShop],
  (selectedToolDomains, selectedShop) => {
    const config = selectedToolDomains['shopifyConfigs'] || {};

    return config[selectedShop] || {};
  }
);

export const getSelectedConfig = createSelector(
  [getOrdersSearchProperties],
  (ordersSearch) => ordersSearch.selectedConfig || {}
);

export const getOrderSearchQuery = createSelector(
  [getOrdersSearchProperties],
  (ordersSearch) => ordersSearch.selectedSearchQuery
);

export const getShopNameFromOrdersByOrderId = (state, orderId) => {
  const allOrders = getOrdersSearchProperties(state).orders;
  // eslint-disable-next-line
  const selectedOrder = allOrders.find((order) => order.id === orderId);

  return selectedOrder ? selectedOrder.shopName : undefined;
};

export const getShippoInfoByShipmentId = createSelector(
  [getOrdersSearchProperties],
  (ordersSearch) => ordersSearch.shippoTrackingInfoByShipmentId || {}
);

export const getSelectedTicketNumber = createSelector([extractSelectedTicketFromState], (selectedTicket) =>
  conversationTypes.channelsWithNoPlatformTicketId.indexOf(selectedTicket.type) > -1
    ? selectedTicket.simplrTicketNumber
    : selectedTicket.platformTicketId
);

export const getIsShopifyInstalled = createSelector(
  [selectSelectedToolDomains],
  (selectedToolDomains) => selectedToolDomains.shopify && selectedToolDomains.shopify.length > 0
);

export const getIsRbiInstalled = createSelector(
  [selectSelectedToolDomains],
  (selectedToolDomains) => selectedToolDomains.rbi && selectedToolDomains.rbi.length > 0
);

export const getIsSalesforceOrderManagerEnabled = createSelector(
  [selectSelectedToolDomains],
  (selectedToolDomains) =>
    selectedToolDomains.salesforceConfig && selectedToolDomains.salesforceConfig.isOrderManagementEnabled
);

export const getIsAmazonInstalled = createSelector(
  [selectSelectedToolDomains],
  (selectedToolDomains) => selectedToolDomains.amazon !== undefined
);

export const getIsCustomSellerInstalled = createSelector(
  [selectSelectedToolDomains],
  (selectedToolDomains) => selectedToolDomains.customseller
);

export const getIsZendeskShopSelected = createSelector([selectSelectedToolDomains], (selectedToolDomains) => {
  const shopifyShops = selectedToolDomains.shopify;
  const allowedHosts = ['zendesk.com', 'support.zendesk.com'];

  if (shopifyShops && shopifyShops.length) {
    const zendeskShop = shopifyShops.find((shopifyShop) => {
      try {
        const host = new URL(shopifyShop).hostname;

        return allowedHosts.some((allowedHost) => host.endsWith(allowedHost));
      } catch (e) {
        return false;
      }
    });

    return !!zendeskShop;
  }

  return false;
});

export const getIsSimplrOrderInstalled = createSelector(
  [selectSelectedToolDomains],
  (selectedToolDomains) => !!(selectedToolDomains.simplrorder && selectedToolDomains.simplrorder.length)
);

export const getIsBigCommerceInstalled = createSelector(
  [selectSelectedToolDomains],
  (selectedToolDomains) => selectedToolDomains.bigCommerce !== undefined
);

export const getIsRevolveInstalled = createSelector(
  [selectSelectedToolDomains],
  (selectedToolDomains) => selectedToolDomains.revolve !== undefined
);

export const getIsCommonOrderSystemInstalled = createSelector(
  [selectSelectedToolDomains],
  (selectedToolDomains) => !!selectedToolDomains.common
);

export const getCommonConfig = createSelector(
  [selectSelectedToolDomains],
  (selectedToolDomains) => selectedToolDomains?.commonConfig ?? {}
);

export const getIsCommonSubscriptionSystemInstalled = createSelector(
  [selectSelectedToolDomains],
  (selectedToolDomains) => !!selectedToolDomains.commonSubscription
);

export const getRbiDomains = createSelector(
  [selectSelectedToolDomains],
  (selectedToolDomains) => selectedToolDomains.rbi
);

export const getSimplrorderDomains = createSelector(
  [selectSelectedToolDomains],
  (selectedToolDomains) => selectedToolDomains.simplrorder || []
);

export const getSimplrorderConfigs = createSelector(
  [selectSelectedToolDomains],
  (selectedToolDomains) => selectedToolDomains.simplrorderConfig
);

export const chooseDefaultSearchSelectedAction = createSelector(
  [
    extractSelectedTicketFromState,
    getSelectedShop,
    getIsShopifyInstalled,
    getIsAmazonInstalled,
    getIsCustomSellerInstalled,
    getIsZendeskShopSelected,
    getIsSimplrOrderInstalled,
    getIsRbiInstalled,
    getIsBigCommerceInstalled,
    getIsSalesforceOrderManagerEnabled,
    getIsRevolveInstalled,
    getIsCommonOrderSystemInstalled,
    getDecisionNodeAtPath,
    getDefaultCOIOrderSearchInput,
  ],
  (
    ticket = {},
    selectedShop,
    isShopifyInstalled,
    isAmazonInstalled,
    isCustomSellerInstalled,
    isZendeskShopSelected,
    isSimplrOrderInstalled,
    isRbiInstalled,
    isBigCommerceInstalled,
    isSalesforceOrderManagerEnabled,
    isRevolveInstalled,
    isCommonOrderSystemInstalled,
    decisionNode,
    defaultCOIOrderSearchInput
  ) => {
    if (!ticket || isEmpty(ticket)) {
      return '';
    }

    if (isCommonOrderSystemInstalled) {
      return defaultCOIOrderSearchInput;
    }

    if (isRbiInstalled) {
      return orderSearchOptions.RBI_EMAIL;
    }

    if (isBigCommerceInstalled) {
      return orderSearchOptions.BIG_COMMERCE_ORDER_EMAIL;
    }

    if (isRevolveInstalled) {
      return orderSearchOptions.REVOLVE_ORDER_EMAIL;
    }

    if (isSalesforceOrderManagerEnabled) {
      if (decisionNode && decisionNode.action === DECISION_NODE_ACTIONS.SELECT_ORDER_AND_LINE_ITEMS_YETI_CANCEL) {
        return orderSearchOptions.SALESFORCE_ORDER_ID;
      }

      return orderSearchOptions.SALESFORCE_ORDER_EMAIL;
    }

    // Shopify and/or Zendesk case
    if (selectedShop || isShopifyInstalled) {
      if (isZendeskShopSelected) {
        if (ticket.customerEmail) {
          return orderSearchOptions.ZENDESK_EMAIL;
        }

        return orderSearchOptions.ZENDESK_ORDER_NUMBER;
      }
      if (ticket.customerEmail) {
        return orderSearchOptions.SHOPIFY_EMAIL;
      }

      return orderSearchOptions.SHOPIFY_ORDER_NUMBER;
    } else if (isAmazonInstalled) {
      return orderSearchOptions.AMAZON_ORDER_NUMBER;
    } else if (isCustomSellerInstalled) {
      return orderSearchOptions.CUSTOM_SELLER_EMAIL;
    } else if (isSimplrOrderInstalled) {
      if (ticket.customerEmail) {
        return orderSearchOptions.SIMPLR_ORDER_EMAIL;
      }

      return orderSearchOptions.SIMPLR_ORDER_ID;
    }

    return '';
  }
);

export const getSelectedOrderSearchAction = createSelector(
  [getOrdersSearchProperties, chooseDefaultSearchSelectedAction],
  (ordersSearch, defaultSearchAction) => {
    return ordersSearch.selectedSearchAction || defaultSearchAction;
  }
);

export const getOrderingSystem = createSelector(
  [getOrdersSearchProperties, getSelectedOrderSearchAction],
  (ordersSearch, selectedSearchAction) => {
    return ordersSearch.orderingSystem || chooseOrderingSystemForSelectedAction(selectedSearchAction);
  }
);

export const getInputsForDynamicIntegrationOrderSearchOption = createSelector(
  [getSelectedOrderSearchAction, getDynamicIntegrationOrderSearchOptions],
  (selectedSearchAction, options) => {
    if (!selectedSearchAction || !selectedSearchAction.includes(orderSearchOptions.COMMON_ORDER_DYNAMIC_INPUT)) {
      return [];
    }

    // Get index off selected option (Ex getCommonOrderDynamicInput0)
    const idx = (selectedSearchAction?.match(/\d/g) || []).join('');

    if (!idx || !options[idx]) {
      return [];
    }

    // options EX [[{ varName: customerEmail }], [{ varName: zipCode }, { varName: orderNumber }]]
    return options[idx];
  }
);

export const isEmailInDynamicIntegrationSearchInput = createSelector(
  [getInputsForDynamicIntegrationOrderSearchOption, getSelectedOrderSearchAction],
  (dynamicIntegrationSearchInputs, selectedSearchAction) => {
    const isDynamicIntegrationSelected = selectedSearchAction?.includes(searchActions.COMMON_ORDER_DYNAMIC_INPUT);

    return (
      isDynamicIntegrationSelected &&
      dynamicIntegrationSearchInputs.some((inputObj) => inputObj.varName === CUSTOMER_EMAIL_SEARCH_INPUT)
    );
  }
);

export const isEmailOnlyDynamicIntegrationSearchInput = createSelector(
  [getInputsForDynamicIntegrationOrderSearchOption, getSelectedOrderSearchAction],
  (dynamicIntegrationSearchInputs, selectedSearchAction) => {
    const isDynamicIntegrationSelected = selectedSearchAction?.includes(searchActions.COMMON_ORDER_DYNAMIC_INPUT);

    return (
      isDynamicIntegrationSelected &&
      dynamicIntegrationSearchInputs.every((inputObj) => inputObj.varName === CUSTOMER_EMAIL_SEARCH_INPUT)
    );
  }
);

export const getAddress = (state) => state.tools.address;

export const getCanceledItems = (state) => state.tools.ordersSearch.canceledItems;

export const getReturnedItems = (state) => state.tools.ordersSearch.returnedItems;

export const getRefundAmount = (state) => state.tools.ordersSearch.refundAmount;

export const getRefundAmountFormatted = (state) => state.tools.ordersSearch.refundAmountFormatted;

export const getOrderTotal = (state) => get(state, 'tools.ordersSearch.orderTotal');

export const getAmazonOrderWarrantyTotalYears = (state) =>
  state.tools.ordersSearch.amazonOrderWarrantyTotalYears || null;

export const getCurrencyCode = (state) => state.tools.ordersSearch.currencyCode || '';

export const getPresentmentCurrencyCode = createSelector(
  [getSelectedOrder, getOrderingSystem],
  (order, orderingSystem) => {
    switch (orderingSystem) {
      case 'shopify':
        return order?.presentment_currency ?? order?.currency;
      default:
        return order?.currency ?? DEFAULT_CURRENCY_CODE;
    }
  }
);

export const getSubscriptionFrequencies = (state) => state.tools.ordersSearch.subscriptionFrequencies || {};

export const getStoreCreditAmount = (state) => state.tools.ordersSearch.storeCreditAmount;

export const getShowConfirmationPagePaths = (state) => {
  return get(state, 'tools.ordersSearch.showConfirmationPagePaths', []);
};

export const getShowConfirmationPage = (state) => {
  const currentPath = extractCurrentDecisionTreePath(state);
  const paths = getShowConfirmationPagePaths(state);

  return paths.includes(currentPath);
};

export const hasSelectedSearchResult = (state) => {
  return getSelectedSearchResultIndex(state) > -1;
};

export const showSelectedSearchResult = (state) => {
  // if no selected result, return false
  if (!hasSelectedSearchResult(state)) {
    return false;
  }

  // check that the tab we are using matches the selected result
  return getSearchTypeOfSelectedAction(state) === getSelectedSearchResultType(state);
};

export const searchAllOpenAndSearchResultSelected = (state) => {
  const selectedSearchResult = getSelectedSearchResult(state);
  const actionsTab = getSearchTypeOfSelectedAction(state);

  return (selectedSearchResult || {}).objectID && actionsTab === searchTypes.SEARCH_ALL;
};

/**
 * state.tools.subscriptionSearch
 */

export const getSubscriptionSearch = (state) => state.tools.subscriptionSearch;

export const selectSubscriptions = createSelector(
  [getSubscriptionSearch],
  (subscriptionSearch) => subscriptionSearch.subscriptions
);

export const selectShopContext = createSelector(
  [getSubscriptionSearch],
  (subscriptionSearch) => subscriptionSearch.shopContext
);

export const getSelectedSubscription = createSelector(
  [getSubscriptionSearch],
  (subscriptionSearch) => subscriptionSearch.selectedSubscription
);

export const getSelectedSubscriptionsIds = createSelector(
  [getSubscriptionSearch],
  (subscriptionSearch) => subscriptionSearch.selectedSubscriptionIds
);

export const getSelectedSubscriptionList = createSelector(
  [selectSubscriptions, getSelectedSubscriptionsIds],
  (subscriptionList, selectedSubscriptionIds) => {
    return subscriptionList.filter((subscription) => selectedSubscriptionIds.includes(subscription.id));
  }
);

export const getSelectedSubscriptionItemNames = createSelector([getSelectedSubscriptionList], (subscriptionList) => {
  return subscriptionList.map((subscription) => subscription.product_title).join(', ');
});

export const getSelectedOrderNames = createSelector([getOrdersSearchProperties], (ordersSearch) => {
  const { selectedItems, selectedOrder } = ordersSearch;
  const items = selectedOrder.line_items.filter((item) => {
    return Object.keys(selectedItems).includes(item.id.toString());
  });

  return items.map((item) => item.title).join(', ');
});

export const getlastOrderInSelectedSubscription = (state) => {
  return state.tools.ordersSearch.lastOrderInSelectedSubscription;
};

export const getFirstSelectedSubscription = createSelector(
  [getSelectedSubscriptionList],
  (selectedSubscriptionList) => {
    return selectedSubscriptionList[0];
  }
);

export const selectSubscriptionSelectedTask = createSelector(
  [getSubscriptionSearch],
  (subscriptionSearch) => subscriptionSearch.selectedTask
);

export const selectSubscriptionSystem = createSelector(
  [getSubscriptionSearch],
  (subscriptionSearch) => subscriptionSearch.subscriptionSystem
);

export const shouldShowSearchButtons = createSelector(
  [extractSelectedWizardMacro, showSelectedSearchResult, searchAllOpenAndSearchResultSelected],
  (selectedMacro, showSelectedResult, searchAllOpenAndItemSelected) => {
    return !(selectedMacro || {}).objectId && !searchAllOpenAndItemSelected && !showSelectedResult;
  }
);

export const getDefaultSearchValueType = createSelector([getSelectedOrderSearchAction], (selectedSearchAction) => {
  if (
    selectedSearchAction === searchActions.SHOPIFY_EMAIL ||
    selectedSearchAction === searchActions.AMAZON_EMAIL ||
    selectedSearchAction === searchActions.ZENDESK_EMAIL ||
    selectedSearchAction === searchActions.SIMPLR_ORDER_EMAIL ||
    selectedSearchAction === searchActions.CUSTOM_SELLER_EMAIL ||
    selectedSearchAction === searchActions.RBI_EMAIL ||
    selectedSearchAction === searchActions.SALESFORCE_ORDER_EMAIL
  ) {
    return conversationTypes.EMAIL;
  }
  if (selectedSearchAction === searchActions.SHOPIFY_PHONE_NUMBER) {
    return conversationTypes.SMS;
  }

  return null;
});

export const getIsCustomerEmailSupportedForCurrentSystem = createSelector(
  [extractSelectedTicketFromState],
  (ticket) => {
    if (!ticket) {
      return false;
    }

    return (
      [
        conversationTypes.ZENDESK,
        conversationTypes.DESK,
        conversationTypes.GORGIAS,
        conversationTypes.ZENDESK_CHAT,
        conversationTypes.SALESFORCE,
        conversationTypes.SALESFORCE_CHAT,
        conversationTypes.GLADLY,
        conversationTypes.GENESYS,
      ].indexOf(ticket.ticketType) > -1
    );
  }
);

export const getDecisionTreeToolSearchType = (state) => {
  const decisionNode = getDecisionNodeAtPath(state);
  // we store the search type in different places for macro wizard search
  // TODO: Add default case
  // eslint-disable-next-line default-case
  switch (decisionNode.action) {
    case DECISION_NODE_ACTIONS.CHOOSE_SUBSCRIPTION_FOR_CANCELLATION:
    case DECISION_NODE_ACTIONS.SELECT_SUBSCRIPTION:
      const subSearchParams = getSubscriptionSearchParams(state);

      return subSearchParams.searchType;
    case DECISION_NODE_ACTIONS.SELECT_ORDER_RBI_LOYALTY_POINTS_ISSUE:
    case DECISION_NODE_ACTIONS.SELECT_ORDER_RBI_FULL_REFUND:
    case DECISION_NODE_ACTIONS.SELECT_ORDER_RBI_PARTIAL_REFUND:
    case DECISION_NODE_ACTIONS.SELECT_ORDER_RBI_COUPON_ISSUE:
    case DECISION_NODE_ACTIONS.SELECT_ITEMS_RBI_COUPON_ISSUE:
    case DECISION_NODE_ACTIONS.SELECT_ORDER_TO_DETECT_CANCELLATIONS:
    case DECISION_NODE_ACTIONS.SELECT_ORDER_AND_LINE_ITEMS_FOR_PRICE_MATCH:
    case DECISION_NODE_ACTIONS.CHOOSE_SUBSCRIPTION_OR_ORDER_FOR_ADDRESS_CHANGE:
    case DECISION_NODE_ACTIONS.SELECT_ORDER_AND_LINE_ITEMS_WITH_NO_RESULT_MACRO:
    case DECISION_NODE_ACTIONS.SELECT_ORDER_AND_LINE_ITEMS:
    case DECISION_NODE_ACTIONS.SELECT_ORDER:
    case DECISION_NODE_ACTIONS.SELECT_ORDER_FOR_CANCELLATION_AND_REFUND:
    case DECISION_NODE_ACTIONS.SELECT_ORDER_FOR_SHIPPING_REFUND:
    case DECISION_NODE_ACTIONS.SELECT_ORDER_ITEMS_FOR_CANCELLATION_AND_REFUND:
    case DECISION_NODE_ACTIONS.SELECT_ORDER_AND_LINE_ITEMS_YETI_CANCEL:
    case DECISION_NODE_ACTIONS.REVOLVE_SELECT_ORDER_FOR_CANCEL:
    case DECISION_NODE_ACTIONS.SELECT_ORDER_ITEMS_FOR_PARTIAL_REFUND:
    case DECISION_NODE_ACTIONS.SELECT_ITEMS_FOR_CANCEL_AND_REFUND_PROCESSING:
    case DECISION_NODE_ACTIONS.SELECT_ITEMS_FOR_REFUND_PROCESSING:
    case DECISION_NODE_ACTIONS.SELECT_ITEMS_FOR_CANCELLATION_PROCESSING:
      return getSelectedOrderSearchAction(state);
  }
};

export const getSelectedSearchAction = (state) => {
  const searchType = selectToolGeneralSearchType(state);

  switch (searchType) {
    case searchTypes.MACRO_WIZARD:
      return getDecisionTreeToolSearchType(state);
    default:
      return getSelectedOrderSearchAction(state);
  }
};

export const getDefaultEmailSearchValue = createSelector(
  [extractSelectedTicketFromState, getIsCustomerEmailSupportedForCurrentSystem],
  (ticket, isCustomerEmailSupportedForCurrentSystem) => {
    if (!ticket) {
      return '';
    } else if (ticket.ticketType === conversationTypes.EMAIL) {
      return ticket.fromChannel;
    } else if (isCustomerEmailSupportedForCurrentSystem) {
      return ticket.customerEmail || '';
    }

    return '';
  }
);

export const getDefaultEmail = createSelector(
  [extractSelectedTicketFromState, getIsCustomerEmailSupportedForCurrentSystem],
  (ticket, isCustomerEmailSupportedForCurrentSystem) => {
    if (!ticket) {
      return '';
    }

    if (ticket.ticketType === conversationTypes.EMAIL) {
      return ticket.fromChannel;
    }

    if (isCustomerEmailSupportedForCurrentSystem) {
      return ticket.customerEmail || '';
    }

    return '';
  }
);

export const chooseDefaultSearchValue = createSelector(
  [extractSelectedTicketFromState, getSelectedSearchAction, getDefaultEmail],
  (ticket, selectedSearchAction, defaultEmail) => {
    if (!ticket) {
      // prevents white screen on resolve
      return '';
    }

    switch (selectedSearchAction) {
      case searchActions.RBI_EMAIL:
      case searchActions.SALESFORCE_ORDER_EMAIL:
      case searchActions.SHOPIFY_EMAIL:
      case searchActions.AMAZON_EMAIL:
      case searchActions.ZENDESK_EMAIL:
      case searchActions.CUSTOM_SELLER_EMAIL:
      case searchActions.SIMPLR_ORDER_EMAIL:
      case searchActions.BIG_COMMERCE_ORDER_EMAIL:
      case searchActions.REVOLVE_ORDER_EMAIL:
      case searchActions.COMMON_ORDER_EMAIL:
        return defaultEmail;

      case searchActions.SHOPIFY_PHONE_NUMBER:
        return ticket.ticketType === conversationTypes.SMS ? ticket.fromChannel.split('+').join('') : '';

      case searchActions.SHOPIFY_ORDER_NUMBER:
        return ticket.associatedOrderNumber ? ticket.associatedOrderNumber.toString() : '';

      case searchActions.AMAZON_ORDER_NUMBER:
        return ticket.amazonOrderNumber ? ticket.amazonOrderNumber.toString() : '';

      case searchActions.SIMPLR_ORDER_SUBSCRIPTION_EMAIL:
      case searchActions.COMMON_SUBSCRIPTION_EMAIL:
        return ticket.customerEmail ? ticket.customerEmail : '';

      case searchActions.SHOPIFY_FULL_NAME:
      case searchActions.ZENDESK_FULL_NAME:
      case searchActions.ZENDESK_EBAY_USERNAME:
      case searchActions.ZENDESK_ORDER_NUMBER:
      case searchActions.COMMON_ORDER_NUMBER:
        return '';

      default:
        return '';
    }
  }
);

export const isIntegrationValueAvailable = createSelector(
  [getSelectedShop, selectSelectedToolDomains, chooseDefaultSearchValue],
  (selectedShop, selectedToolDomains, defaultSearchValue) =>
    (selectedShop ||
      selectedToolDomains.shopify ||
      selectedToolDomains.simplrorder ||
      selectedToolDomains.amazon ||
      selectedToolDomains.customseller) &&
    defaultSearchValue !== ''
);

export const getMockOrderManagement = createSelector([extractSelectedTicketBusinessDetailsFromState], (business) =>
  get(business, 'extraBusinessInfo.mockOrderInfo.mockOrderManagement', null)
);

export const showOrdersButton = createSelector(
  [
    getIsShopifyInstalled,
    getIsAmazonInstalled,
    getIsCustomSellerInstalled,
    getIsZendeskShopSelected,
    getIsSimplrOrderInstalled,
    getIsRbiInstalled,
    getMockOrderManagement,
    getIsSalesforceOrderManagerEnabled,
  ],
  (
    isShopifyInstalled,
    isAmazonInstalled,
    isCustomSellerInstalled,
    isZendeskShopSelected,
    isSimplrOrderInstalled,
    isRbiInstalled,
    mockOrderManagement,
    isSalesforceOrderManagerEnabled
  ) => {
    const anyIntegrationAvailable =
      isShopifyInstalled ||
      isAmazonInstalled ||
      isCustomSellerInstalled ||
      isSimplrOrderInstalled ||
      isRbiInstalled ||
      isSalesforceOrderManagerEnabled;

    return anyIntegrationAvailable || mockOrderManagement || false;
  }
);

export const getIsSearchButtonsActive = createSelector(
  [isSpecialist, extractSelectedTicketFromState, getUser],
  (_isSpecialist, ticket, user) =>
    _isSpecialist || (ticket && ticket.expertId === user.id && ticket.ticketStatus === TICKET_STATUS_ASSIGNED)
);

export const showSearchAllButton = createSelector(
  [isSpecialist, extractSelectedTicketBusinessDetailsFromState],
  (_isSpecialist, business) => {
    const historicalTicketSearch = get(business, 'historicalTicketSearch');

    return historicalTicketSearch === EXPERT_AND_ADMIN || (_isSpecialist && historicalTicketSearch === ADMIN);
  }
);

export const selectToolGeneralSearchType = createSelector(
  [
    getGeneral,
    selectSelectedToolDomains,
    showOrdersButton,
    showSearchAllButton,
    getSelectedShop,
    getIsSearchButtonsActive,
  ],
  (general, selectedToolDomains, showOrders, showSearchAll, selectedShop, searchButtonsActive) => {
    const shopSearchIntegrationAvailable =
      selectedShop || selectedToolDomains.amazon || selectedToolDomains.customseller;

    if (general.searchType) {
      return general.searchType;
    }

    const notShowSearchAll =
      shopSearchIntegrationAvailable && showOrders && searchButtonsActive ? searchTypes.SEARCH_ORDERS : '';

    return showSearchAll ? searchTypes.SEARCH_ALL : notShowSearchAll;
  }
);

// SEARCH ALL LOCAL
export const getSearchAllObject = (state) => state.tools.ticketSearch || {};
export const getSearchAllQuery = createSelector([getSearchAllObject], (searchAll) => searchAll.query);
export const getSearchAllMacroResults = createSelector(
  [getSearchAllObject],
  (searchAll) => searchAll.macroResults || []
);
export const getSearchAllKBResults = createSelector([getSearchAllObject], (searchAll) => searchAll.kbResults || []);
export const getSearchAllTicketResults = createSelector(
  [getSearchAllObject],
  (searchAll) => searchAll.ticketResults || []
);
export const getSearchAllError = createSelector([getSearchAllObject], (searchAll) => searchAll.error || '');

export const noSearchAllResultsAndQueryIsEmpty = createSelector(
  [getSearchAllMacroResults, getSearchAllKBResults, getSearchAllTicketResults, getSearchAllQuery],
  (macroResults, kbResults, ticketResults, query) => {
    return macroResults.length === 0 && kbResults.length === 0 && ticketResults.length === 0 && query === '';
  }
);

export const getLineItemsOfSelectedOrder = createSelector(
  [getOrdersSearchProperties, getOrderingSystem],
  (ordersSearch, orderingSystem) => {
    const { selectedOrder } = ordersSearch;
    if (isEmpty(selectedOrder)) {
      return [];
    }
    switch (orderingSystem) {
      case 'common':
        return selectedOrder.orderItems;
      case 'bigCommerce':
        return selectedOrder.product_items;
      case 'shopify':
        return selectedOrder.line_items;
      case 'custom_rbi':
        return selectedOrder.cart;
      case 'salesforce':
        return selectedOrder.orderItems;
      case 'revolve':
        return selectedOrder.lineItems;
      default:
        return [];
    }
  }
);

export const getSelectedLineItemsIds = createSelector([getOrdersSearchProperties], (ordersSearch) => {
  const { selectedItems, selectedOrder } = ordersSearch;
  if (isEmpty(selectedItems) || isEmpty(selectedOrder)) {
    return [];
  }
  const selectedItemIds = [];
  for (let itemId in selectedItems) {
    const isActive = selectedItems[itemId];
    if (isActive) {
      selectedItemIds.push(itemId);
    }
  }

  return selectedItemIds;
});

export const getSelectedLineItemsQuantity = createSelector([getOrdersSearchProperties], (ordersSearch) => {
  return ordersSearch.selectedItemsQuantity ?? {};
});

export const getOrderShipmentsOfSelectedOrder = createSelector(
  [getSelectedOrder, getOrderingSystem],
  (selectedOrder, orderingSystem) => {
    switch (orderingSystem) {
      case 'common':
        return selectedOrder.shipments || [];
      case 'bigCommerce':
        return selectedOrder.shipment_items || [];
      case 'shopify':
        return selectedOrder.fulfillments || [];
      case 'revolve':
        return [];
      default:
        return [];
    }
  }
);

export const getShippingAddressOfSelectedOrder = createSelector(
  [getSelectedOrder, getOrderingSystem],
  (selectedOrder, orderingSystem) => {
    if (orderingSystem === 'shopify') {
      return selectedOrder?.shipping_address ?? {};
    }

    return selectedOrder?.shippingAddress ?? {};
  }
);

const getItemByIdFinder = (orderingSystem, lineItems) => (itemId) =>
  lineItems.find((item) => String(getItemId(item, orderingSystem)) === String(itemId));

const fillItemQuantity = (lineItem, orderingSystem, selectedItemsQuantity) => {
  const id = getItemId(lineItem, orderingSystem);
  const customQuantity = selectedItemsQuantity[id];
  if (customQuantity) {
    return { ...lineItem, [getMappedOrderingSystemPropertyName('quantity', orderingSystem)]: customQuantity };
  }

  return lineItem;
};

export const getSelectedLineItems = createSelector(
  [getLineItemsOfSelectedOrder, getSelectedLineItemsIds, getSelectedLineItemsQuantity, getOrderingSystem],
  (lineItems, selectedItemIds, selectedItemsQuantity, orderingSystem) => {
    switch (orderingSystem) {
      case 'common':
      case 'shopify':
      case 'bigCommerce':
      case 'revolve':
        return selectedItemIds.map((itemId) => {
          const findItemByID = getItemByIdFinder(orderingSystem, lineItems);
          const lineItem = findItemByID(itemId);

          return fillItemQuantity(lineItem, orderingSystem, selectedItemsQuantity);
        });
      case 'salesforce':
        return selectedItemIds.map((itemId) => lineItems.find((item) => String(item.Id) === String(itemId)));
      case 'custom_rbi':
        // TO DO: This is strange and should be corrected
        return selectedItemIds.map(() => {
          return [];
        });
      default:
        return [];
    }
  }
);

export const hasLineItemsInOrderSelected = createSelector([getSelectedLineItems], (lineItems) => {
  return lineItems.length > 0;
});

export const getOrderCreationDate = createSelector(
  [getSelectedOrder, getOrderingSystem],
  (selectedOrder, orderingSystem) => {
    switch (orderingSystem) {
      case 'bigCommerce':
        return selectedOrder.date_created;
      case 'revolve':
        return selectedOrder.createdAt;
      default:
        return selectedOrder.created_at;
    }
  }
);

export const getBillingZipCodeOfSelectedOrder = createSelector(
  [getSelectedOrder, getOrderingSystem],
  (selectedOrder, orderingSystem) => {
    switch (orderingSystem) {
      case 'shopify':
        return get(selectedOrder, 'billing_address.zip', undefined);
      default:
        return undefined;
    }
  }
);

export const getEmailOfSelectedOrder = createSelector(
  [getSelectedOrder, getOrderingSystem],
  (selectedOrder, orderingSystem) => {
    switch (orderingSystem) {
      case 'shopify':
        return selectedOrder.email;
      case 'bigCommerce':
        return get(selectedOrder, 'billing_address.email', null);
      default:
        return undefined;
    }
  }
);

// get a the list of selected line items grouped by their shipment status
// The grouping are determined by the ID of the shipment (orderShipment.increment_id )
// Or by the ship date. The reason is so that we can group these items together in the snippet
export const getSelectedLineItemsByShipmentStatus = createSelector(
  [getSelectedLineItems, getOrderShipmentsOfSelectedOrder, getOrderingSystem],
  (lineItems, orderShipments, orderingSystem) => {
    const itemsByShipmentStatus = {};

    // this function adds the line items to the array corresponding to their shipping status
    const addToShipmentStatus = (lineItem, shipmentStatus) => {
      itemsByShipmentStatus[shipmentStatus] = itemsByShipmentStatus[shipmentStatus] || [];
      itemsByShipmentStatus[shipmentStatus].push(lineItem);
    };

    lineItems.forEach((lineItem) => {
      let foundInShipment = false;
      // TODO: May need check to make sure single item does appear it multiple shipments
      // first check our order shipments
      // orderShipments = fulfillments in Shopify
      orderShipments.forEach((orderShipment) => {
        switch (orderingSystem) {
          case 'shopify':
            orderShipment.line_items.forEach((shipmentItem) => {
              if (shipmentItem.id === lineItem.id) {
                const shipmentData = { shipmentId: orderShipment.id };
                const shipmentStatus = JSON.stringify(shipmentData);
                addToShipmentStatus(lineItem, shipmentStatus);
                foundInShipment = true;
              }
            });
            break;
          case 'bigCommerce':
            orderShipment.items.forEach((shipmentItem) => {
              if (shipmentItem.order_product_id === lineItem.id) {
                const shipmentData = { shipmentId: orderShipment.order_product_id };
                const shipmentStatus = JSON.stringify(shipmentData);
                addToShipmentStatus(lineItem, shipmentStatus);
                foundInShipment = true;
              }
            });
            break;
          default:
            return;
        }
      });

      // if not in order shipment, then check shipping date
      if (!foundInShipment) {
        const shipmentData = { shipping_date: lineItem.shipping_date };
        // encode the shipping data as a string so we can use it as a primary key
        const shipmentStatus = JSON.stringify(shipmentData);
        addToShipmentStatus(lineItem, shipmentStatus);
      }
    });

    return itemsByShipmentStatus;
  }
);

// get a the list of shipped items grouped by their shipment status
// The grouping are determined by the ID of the shipment (orderShipment.increment_id )
// Or by the ship date. The reason is so that we can group these items together in the snippet
export const getShippedItemsByShipmentStatusForShopify = createSelector(
  [getSelectedLineItems, getOrderShipmentsOfSelectedOrder, getOrderingSystem],
  (lineItems, orderShipments, orderingSystem) => {
    const itemsByShipmentStatus = {};
    // this function adds the shipped items to the array corresponding to their shipping status
    const addToShipmentStatus = (shipmentItem, shipmentStatus) => {
      itemsByShipmentStatus[shipmentStatus] = itemsByShipmentStatus[shipmentStatus] || [];
      itemsByShipmentStatus[shipmentStatus].push(shipmentItem);
    };

    lineItems.forEach((lineItem) => {
      let foundInShipment = false;
      // first check our order shipments
      // orderShipments = fulfillments in Shopify
      orderShipments.forEach((orderShipment) => {
        switch (orderingSystem) {
          case 'shopify':
            const shipmentItems = get(orderShipment, 'shipmentItems', orderShipment.line_items);
            shipmentItems.forEach((shipmentItem) => {
              if (shipmentItem.id === lineItem.id || String(shipmentItem.lineItemKey) === lineItem.id) {
                const shipmentData = { shipmentId: orderShipment.id || orderShipment.shipmentId };
                const shipmentStatus = JSON.stringify(shipmentData);
                addToShipmentStatus(shipmentItem, shipmentStatus);
                foundInShipment = true;
              }
            });
            break;
          default:
            return;
        }
      });

      // if not in order shipment, then check shipping date
      if (!foundInShipment) {
        const shipmentData = { shipping_date: lineItem.shipping_date };
        // encode the shipping data as a string so we can use it as a primary key
        const shipmentStatus = JSON.stringify(shipmentData);
        addToShipmentStatus(lineItem, shipmentStatus);
      }
    });

    return itemsByShipmentStatus;
  }
);

export const getOrderId = (selectedOrder, orderingSystem) => {
  switch (orderingSystem) {
    case 'shopify':
    case 'bigCommerce':
    case 'revolve':
      return getMappedValueFromOrderOrItem(selectedOrder, 'orderId', orderingSystem);
    default:
      return null;
  }
};

export const getStatusOfItemsOfSelectedOrderByItemId = (state) => {
  const orderingSystem = getOrderingSystem(state);
  switch (orderingSystem) {
    case 'common':
      return getStatusOfItemsOfCommonSelectedOrder(state);
    case 'shopify':
      return getStatusOfItemsOfSelectedOrderForShopify(state);
    case 'bigCommerce':
      return getStatusOfItemsOfSelectedOrderForBigCommerce(state);
    case 'revolve':
      return getStatusOfItemsOfSelectedOrderForRevolve(state);
    default:
      return null;
  }
};

export const getStatusOfItemsOfSelectedOrderForBigCommerce = createSelector(
  [getLineItemsOfSelectedOrder, getSelectedOrder, getShippoInfoByShipmentId],
  (lineItems = [], selectedOrder, shippoInfoByShipmentId) => {
    const statusByItemId = {};
    if (lineItems.length) {
      lineItems.forEach((lineItem) => {
        let foundInShipment = false;
        if (Array.isArray(selectedOrder.shipment_items)) {
          selectedOrder.shipment_items.forEach((fulfillment) => {
            if (Array.isArray(fulfillment.items)) {
              fulfillment.items.forEach((fulfillment_item) => {
                if (lineItem.id === fulfillment_item.order_product_id) {
                  foundInShipment = true;
                  const shippoInfo = shippoInfoByShipmentId[fulfillment.id];
                  let shipmentStatus = get(shippoInfo, 'tracking_status.status');
                  if (shipmentStatus === 'DELIVERED' && itemIsUPSSurepost(shippoInfo)) {
                    // if an item is surepost, 'delivered' status is unreliable for UPS orders
                    statusByItemId[lineItem.id] = 'unknown';
                  } else {
                    if (shipmentStatus) {
                      statusByItemId[lineItem.id] = getReadableStatusFromShippoStatus(shipmentStatus);
                    } else {
                      statusByItemId[lineItem.id] = 'unknown';
                    }
                  }
                }
              });
            }
          });
        }

        // if not in order shipment, must be unfulfilled
        if (!foundInShipment) {
          statusByItemId[lineItem.id] = 'unfulfilled';
        }
      });
    }

    return statusByItemId;
  }
);

export const getStatusOfItemsOfSelectedOrderForRevolve = createSelector(
  [getLineItemsOfSelectedOrder],
  (lineItems = []) => {
    const statusByItemId = {};
    if (lineItems.length) {
      lineItems.forEach((lineItem) => {
        statusByItemId[lineItem.id] = lineItem.status;
      });
    }

    return statusByItemId;
  }
);

export const getStatusOfItemsOfCommonSelectedOrder = createSelector([getLineItemsOfSelectedOrder], (lineItems = []) => {
  const statusByItemId = {};
  if (lineItems.length) {
    lineItems.forEach((lineItem) => {
      statusByItemId[lineItem.itemId] = lineItem.itemShippingStatus;
    });
  }

  return statusByItemId;
});

export const getStatusOfItemsOfSelectedOrderForShopify = createSelector(
  [getLineItemsOfSelectedOrder, getSelectedOrder, getShippoInfoByShipmentId],
  (lineItems = [], selectedOrder, shippoInfoByShipmentId) => {
    const statusByItemId = {};
    if (lineItems.length) {
      lineItems.forEach((lineItem) => {
        let foundInShipment = false;
        if (selectedOrder.fulfillments) {
          selectedOrder.fulfillments.forEach((fulfillment) => {
            const orderItems = get(fulfillment, 'shipmentItems', fulfillment.line_items);
            orderItems.forEach((fulfillment_item) => {
              if (lineItem.id === fulfillment_item.id || String(lineItem.id) === fulfillment_item.lineItemKey) {
                foundInShipment = true;
                const shippoInfo = shippoInfoByShipmentId[fulfillment.id];
                let shipmentStatus = get(shippoInfo, 'tracking_status.status');
                if (shipmentStatus === 'DELIVERED' && itemIsUPSSurepost(shippoInfo)) {
                  // if an item is surepost, 'delivered' status is unreliable for UPS orders
                  statusByItemId[lineItem.id] = 'unknown';
                } else {
                  if (shipmentStatus) {
                    statusByItemId[lineItem.id] = getReadableStatusFromShippoStatus(shipmentStatus);
                  } else if (fulfillment.shipment_status) {
                    statusByItemId[lineItem.id] = getReadableStatusFromShopifyStatus(fulfillment.shipment_status);
                  } else {
                    statusByItemId[lineItem.id] = 'unknown';
                  }
                }
              }
            });
          });
        }

        // if not in order shipment, must be unfulfilled
        if (!foundInShipment) {
          statusByItemId[lineItem.id] = 'unfulfilled';
        }
      });
    }

    return statusByItemId;
  }
);

export const getShippingDescriptionOfSelectedOrder = createSelector(
  [getSelectedOrder, getOrderingSystem],
  (order, orderingSystem) => {
    // TODO: Add default case
    // eslint-disable-next-line default-case
    switch (orderingSystem) {
      case 'common':
        return order.orderStatus;
      case 'bigCommerce':
        return order.status;
      case 'shopify':
        // return a single value for display purposes
        return get(order, 'shipping_lines[0].title');
    }
  }
);

export const getOrderDateOfSelectedOrder = createSelector(
  [getSelectedOrder, getOrderingSystem],
  (order, orderingSystem) => {
    switch (orderingSystem) {
      case 'common':
        return order.createdAt;
      case 'bigCommerce':
        return order.date_created;
      default:
        return order.created_at;
    }
  }
);

export const getTrackingStatusByItemId = createSelector(
  [getOrderShipmentsOfSelectedOrder, getShippoInfoByShipmentId, getOrderingSystem, getLineItemsOfSelectedOrder],
  (orderShipments, shippoInfoByShipmentId, orderingSystem, orderItems) => {
    const trackingInfoByItemId = {};
    // TODO: Add default case
    // eslint-disable-next-line default-case
    switch (orderingSystem) {
      case 'common':
        orderItems.forEach((item) => {
          // Items almost always have one shipment
          trackingInfoByItemId[item.itemId] = get(item, 'shipments', []);
        });
        break;
      case 'shopify':
        // orderShipments = fulfillments for shopify
        orderShipments.forEach((orderShipment) => {
          const shipmentItems = get(orderShipment, 'shipmentItems', orderShipment.line_items);
          shipmentItems.forEach((shipmentItem) => {
            // tracking status is stored on fulfillment, not line_items
            const trackingInfoByItemIdKey = get(shipmentItem, 'lineItemKey', shipmentItem.id);
            trackingInfoByItemId[trackingInfoByItemIdKey] = [orderShipment];
          });
        });
        break;
      case 'bigCommerce':
        orderShipments.forEach((orderShipment) => {
          const { id } = orderShipment;
          const shippoInfo = shippoInfoByShipmentId[id];
          if (Array.isArray(orderShipment.items)) {
            orderShipment.items.forEach((shipmentItem) => {
              trackingInfoByItemId[shipmentItem.order_product_id] = [shippoInfo];
            });
          }
        });
        break;
    }

    return trackingInfoByItemId;
  }
);

const getItemsInShipment = (orderItems, orderShipment, orderingSystem) => {
  switch (orderingSystem) {
    case 'common':
      if (!orderShipment?.shipmentId) {
        return orderShipment?.shipmentItems ?? [];
      }

      return orderItems.filter((item) =>
        item.shipments.some((shipment) => shipment.shipmentId === orderShipment.shipmentId)
      );
    case 'bigCommerce':
      return orderShipment.items || [];
    default:
      return orderShipment.line_items || [];
  }
};

export const getCarrierAndTrackingNumberByItemId = createSelector(
  [getOrderShipmentsOfSelectedOrder, getOrderingSystem, getLineItemsOfSelectedOrder],
  (orderShipments, orderingSystem, orderItems) => {
    if (isEmpty(orderShipments)) {
      return {};
    }
    const carrierAndTrackingNumberByItemId = {};

    orderShipments.forEach((orderShipment) => {
      const carrier_code = getCarrierCodeOfShipment(orderShipment, orderingSystem);
      const tracking_number = getTrackingNumberOfShipment(orderShipment, orderingSystem);
      const items = getItemsInShipment(orderItems, orderShipment, orderingSystem);
      items.forEach((shipmentItem) => {
        // TO DO: This is sloppy - clean it up
        const itemId = ['bigCommerce'].includes(orderingSystem)
          ? // for bigCommerce items in Shipment has id -> order_product_id
            shipmentItem.order_product_id
          : getItemId(shipmentItem, orderingSystem);
        carrierAndTrackingNumberByItemId[itemId] = { carrier_code, tracking_number };
      });
    });

    return carrierAndTrackingNumberByItemId;
  }
);

export const getSelectedOrderNumber = createSelector([getSelectedOrder, getOrderingSystem], (order, orderingSystem) => {
  return getMappedValueFromOrderOrItem(order, 'orderNumber', orderingSystem);
});
export const getSelectedOrderId = createSelector([getSelectedOrder, getOrderingSystem], (order, orderingSystem) => {
  return getMappedValueFromOrderOrItem(order, 'orderId', orderingSystem);
});

export const getSelectedOrderName = createSelector([getSelectedOrder, getOrderingSystem], (order, orderingSystem) => {
  switch (orderingSystem) {
    case 'shopify':
      return order.name;
    default:
      return null;
  }
});

export const getSelectedOrderCustomerEmail = createSelector(
  [getSelectedOrder, getOrderingSystem],
  (order, orderingSystem) => {
    return getMappedValueFromOrderOrItem(order, 'customerEmail', orderingSystem);
  }
);

export const getSelectedOrderShippingAmount = (state) => get(state, 'tools.ordersSearch.refundShippingAmount');

export const getOrderModificationsAreAllowed = createSelector(
  [isSelectedTicketTaken, isSpecialist],
  (isSelectedTicketTakenValue, isSpecialistValue) => {
    return isSelectedTicketTakenValue || isSpecialistValue;
  }
);

const getRbiStoreFullAddress = (storeAddress) => {
  if (isEmpty(storeAddress)) {
    return '';
  }

  return Object.values(storeAddress)
    .filter((string) => !!string)
    .join(', ');
};

export const getRbiRefundedOrderInfoWidgetData = createSelector(
  [getSelectedOrder, getRbiApprovedRefundAmount],
  (selectedOrder, postRefundAmount) => {
    const refundInfo = selectedOrder.refund || {};
    const { refundedItems, approvedAmount } = refundInfo;

    let refundedAmount = 0;
    let refundedItemsNamesList = [];

    if (approvedAmount) {
      // postRefundAmount - stored after refund is successful, includes taxes
      // approvedAmount - stored before refund, does not include taxes
      refundedAmount = postRefundAmount || approvedAmount;
    }

    if (Array.isArray(refundedItems) && refundedItems.length) {
      refundedItems.forEach((item) => {
        if (item.refundQuantity > 0) {
          refundedItemsNamesList.push(item.name);
        }
      });
    }

    const currencyCode = get(selectedOrder, 'currency.code', 'USD');

    // convert cents to dollars string
    if (refundedAmount) {
      refundedAmount = priceFormat(formatCentsToDollars(refundedAmount), currencyCode);
    }

    const customerName = get(selectedOrder, 'userDetails.name', '');
    let transactionDateTime = get(selectedOrder, 'refund.transactionDateTime', '');

    if (transactionDateTime) {
      transactionDateTime = formatOrderDateToLocalTime(transactionDateTime, 'custom_rbi');
    }

    const storeAddress = getRbiStoreFullAddress(selectedOrder.storeAddress);

    return {
      customerName,
      refundedAmount,
      refundedItemsNamesList,
      transactionDateTime,
      storeAddress,
    };
  }
);

const getRbiPaymentCardType = (order) => {
  const cardType = get(order, 'payment.cardType');

  if (!cardType) {
    return 'N/A';
  } else if (cardType === 'GIFT_CARD') {
    return 'Gift Card';
  }

  return 'Credit Card';
};

export const getRbiRefundInfoWidgetData = createSelector(
  [
    getSelectedOrder,
    getSelectedRbiItemsForRefund,
    getRefundAmountDetails,
    getDecisionNodeAtPath,
    getRbiCouponsInfo,
    getRbiApprovedRefundAmount,
  ],
  (selectedOrder, selectedRbiItemsForRefund, refundAmountDetails, decisionNode, couponsInfo, postRefundAmount) => {
    let selectedItems = [];
    const currencyCode = get(selectedOrder, 'currency.code', 'USD');

    if (Array.isArray(selectedRbiItemsForRefund)) {
      selectedItems = selectedRbiItemsForRefund.reduce((list, item) => {
        if (item.quantity) {
          list.push({
            name: item.name,
            price: priceFormat(formatCentsToDollars(item.price), currencyCode),
            points: item.points,
          });
        }

        return list;
      }, []);
    }

    let refundAmountsList = [];

    for (const amountLabel in refundAmountDetails) {
      if (amountLabel === 'totalCents') {
        // postRefundAmount - stored after refund is successful, includes taxes
        // refundAmountDetails - stored before refund, does not include taxes
        const refundAmount = postRefundAmount || refundAmountDetails[amountLabel];
        refundAmountsList.push({
          name: 'Total',
          value: priceFormat(formatCentsToDollars(refundAmount), currencyCode),
        });
      }

      if (amountLabel === 'totalPoints') {
        refundAmountsList.push({
          name: 'Total Loyalty Points',
          value: refundAmountDetails[amountLabel],
        });
      }
    }

    const storeAddress = getRbiStoreFullAddress(selectedOrder.storeAddress);
    const customerName =
      get(selectedOrder, 'userDetails.name', null) || get(couponsInfo, 'loyaltyInfo.customer.name', 'N/A');
    const deliveryStatus = normalizeSnakeCase(get(selectedOrder, 'delivery.status', ''));
    const couponType = get(decisionNode, 'couponType.name', '');
    const pointsAmount = get(decisionNode, 'pointsAmount', '');

    const paymentCardType = getRbiPaymentCardType(selectedOrder);

    return {
      customerName,
      refundAmountsList,
      selectedItems,
      storeAddress,
      deliveryStatus: deliveryStatus || 'N/A',
      couponType,
      pointsAmount,
      paymentCardType,
    };
  }
);

export const getRbiLoyaltyPointsInfo = createSelector(
  [getSelectedOrder, getRbiCouponsInfo, getDecisionNodeAtPath],
  (selectedOrder, couponsInfo, decisionNode) => {
    const customerName = get(couponsInfo, 'loyaltyInfo.customer.name', 'N/A');
    const storeAddress = getRbiStoreFullAddress(selectedOrder.storeAddress);
    const deliveryStatus = normalizeSnakeCase(get(selectedOrder, 'delivery.status')) || 'N/A';
    const customerCountry = get(couponsInfo, 'loyaltyInfo.customer.isoCountryCode', 'N/A');

    const pointsToIssueAmount = get(decisionNode, 'pointsAmount'); // points that will be issued to the customer after api call
    const pointsAvailableAmount = get(couponsInfo, 'loyaltyInfo.loyaltyCard.details.points', '-'); // points that customer has on the card balance
    const cardId = get(couponsInfo, 'loyaltyInfo.loyaltyCard.cardId');
    const maskedLoyaltyCardId = cardId ? `XXXX-XXXX-${cardId.slice(cardId.length - 4)}` : 'N/A';
    const currency = get(selectedOrder, 'currency.symbol', '');
    const transactionsHistory = get(couponsInfo, 'loyaltyInfo.loyaltyCard.loyaltyTransactions.transactions', []);
    const recentTransactionsList = mapTransactionHistoryForDisplay(transactionsHistory, currency);

    return {
      customerName,
      storeAddress,
      deliveryStatus,
      customerCountry,
      pointsToIssueAmount,
      pointsAvailableAmount,
      maskedLoyaltyCardId,
      recentTransactionsList,
    };
  }
);

const mapTransactionHistoryForDisplay = (transactionsList, currency) => {
  // api returns 40 most recent transactions sorted by timestamp
  // for display we need the 15 most recent ones
  return transactionsList.slice(0, 15).map((transaction) => {
    const {
      timestamp,
      city,
      stateOrProvince,
      basketTotal,
      basketTaxes,
      pointsUsed,
      pointsEarned,
      rollsEarned,
      rewardRedeemed,
      discountAmount,
      isCustomerServiceVisit,
      appliedOffers,
      items,
      transactionId,
      scanAndPay,
    } = transaction;

    let location;

    if (isCustomerServiceVisit) {
      location = 'SUPPORT';
    } else if (city && stateOrProvince) {
      location = `${city}, ${stateOrProvince}`;
    } else {
      location = '-';
    }

    return {
      timestamp: formatOrderDateToLocalTime(timestamp, 'custom_rbi'),
      location,
      currency,
      total: `${formatCentsToDollars(basketTotal + basketTaxes)}`,
      pointsUsed: pointsUsed || '-',
      pointsEarned: pointsEarned || '-',
      rollsEarned: rollsEarned || '-',
      rewardUsed: rewardRedeemed ? `Yes ($${formatCentsToDollars(discountAmount)})` : 'No',
      isCustomerServiceVisit,
      appliedOffers,
      items,
      scanAndPay, // this field exists only for Tim Hortons
      transactionId: transactionId || `${Math.floor(Math.random() * 1000)}`, // default id to a random string in case RBI doesn't always provide an id
    };
  });
};

export const getRbiExistingCouponsInfo = createSelector(
  [getRbiCouponsInfo, getRbiAssignedOffers],
  (existingCoupons, assignedOffers) => {
    let redeemedCoupons = [];
    let assignedCoupons = [];

    if (Array.isArray(existingCoupons.couponRedemptions)) {
      redeemedCoupons = existingCoupons.couponRedemptions.map((coupon) => ({
        ...coupon,
        name: coupon.name || 'N/A',
        issuedBy: coupon.supportCognitoId ? 'SUPPORT' : 'N/A',
        assignedAt: formatOrderDateToLocalTime(coupon.assignedAt, 'custom_rbi') || 'N/A',
        redeemedOn: formatOrderDateToLocalTime(coupon.redeemedOn, 'custom_rbi') || 'N/A',
      }));
    }

    if (Array.isArray(assignedOffers)) {
      assignedCoupons = assignedOffers.map((coupon) => ({
        ...coupon,
        name: coupon.name || 'N/A',
        issuedBy: isCouponIssuedBySupport(coupon) ? 'SUPPORT' : 'N/A',
        createdAt: formatOrderDateToLocalTime(coupon.assignedDate, 'custom_rbi') || 'N/A',
        expiresAt: formatOrderDateToLocalTime(coupon.expiryDate, 'custom_rbi') || 'N/A',
      }));
    }

    return {
      assignedCoupons,
      redeemedCoupons,
    };
  }
);

export const getRbiExistingSupportCouponsCount = (state) => {
  const issuedCoupons = getRbiAssignedOffers(state);

  if (isEmpty(issuedCoupons) || !Array.isArray(issuedCoupons)) {
    return 0;
  }

  const last4WeekStart = moment().startOf('day').subtract(4, 'week').utc();
  const currentTime = moment.utc();

  return issuedCoupons.reduce((result, coupon) => {
    const isInLast4Week = moment(coupon.assignedDate).utc().isBetween(last4WeekStart, currentTime);

    const isIssuedBySupport = isCouponIssuedBySupport(coupon);

    if (isIssuedBySupport && isInLast4Week) {
      result++;
    }

    return result;
  }, 0);
};

export const getRbiRutrEventsInfo = (state) => {
  const rutrEventsList = get(state, 'tools.ordersSearch.rbiRutrEvents', []);

  return rutrEventsList.map((event) => ({
    ...event,
    createdAt: formatOrderDateToLocalTime(event.createdAt, 'custom_rbi'),
    prizeId: event.prizeId || 'N/A',
  }));
};

export const getToolsLoading = (state) => state.ui.tools.general.toolsLoading;

export const getSelectedTicketShopNamesFromBrand = (state) => {
  const shopifyConfigs = selectCurrentShopifyConfigs(state);
  const simplrOrderConfigs = getSimplrorderConfigs(state);
  const platformCommerceConfig = shopifyConfigs || simplrOrderConfigs;
  if (!platformCommerceConfig) {
    return [];
  }

  const selectedTicket = extractSelectedTicketFromState(state);
  const ticketBrand = selectedTicket && selectedTicket.brand;

  const entries = Object.entries(platformCommerceConfig).filter((entry) => {
    const entryBrands = get(entry, '[1].brands', []);

    // If ticket has no brand or store carries no brands or store carries ticket brand, select store.
    return !ticketBrand || !Array.isArray(entryBrands) || !entryBrands.length || entryBrands.includes(ticketBrand);
  });

  // If stores selected - return them.
  if (entries.length) {
    return entries.map((entry) => entry[0]);
  }

  // If no stores selected - return all stores.
  // This is a possible scenario if ticket has a brand, and all stores carry some brands, but none carry the ticket brand.
  return Object.entries(platformCommerceConfig).map((entry) => entry[0]);
};

export const getDTreeLoadingState = (state) =>
  state.ui.loading.isLoading && state.ui.loading.component === 'decisionTree';

export const getSearchPageLoadingState = (state) =>
  (state.ui.loading.isLoading && state.ui.loading.component === 'searchPage') || state.ui.loading.unique?.searchPage;

export const getMessagesLoadingState = (state) =>
  state.ui.loading.isLoading && state.ui.loading.component === 'messages';

export const getExtraLastMileInfo = _createSelector(
  [getOrdersSearchProperties],
  (ordersSearch) => ordersSearch.extraLastMileInfo
);

export const getSelectedRbiItemsForLastMileRefund = _createSelector(
  [getSelectedRbiItemsForRefund],
  (selectedRbiItemsForRefund) => {
    return selectedRbiItemsForRefund.filter((item) => item.quantity > 0);
  }
);

export const getIsReturnsSystemInstalled = createSelector(
  [selectSelectedToolDomains],
  (selectedToolDomains) => !!selectedToolDomains.commonReturns
);

export const chooseDefaultReturnsSearchSelectedAction = createSelector(
  [extractSelectedTicketFromState, getIsReturnsSystemInstalled, getDecisionNodeAtPath],
  (ticket = {}, isReturnsSystemInstalled) => {
    if (!ticket || isEmpty(ticket)) {
      return '';
    }

    if (!isReturnsSystemInstalled) {
      return '';
    }

    return returnsSearchOptions.SEARCH_RETURNS_BY_ORDER_NUMBER;
  }
);

export const getReturnsSearchProperties = (state) => state.tools.returnsSearch || {};

export const getSelectedReturn = createSelector(
  [getReturnsSearchProperties],
  (returnsSearch) => returnsSearch.selectedReturn || {}
);

export const getSelectedReturnsSearchAction = createSelector(
  [getReturnsSearchProperties, chooseDefaultReturnsSearchSelectedAction],
  (returnsSearch, defaultSearchAction) => {
    return returnsSearch.selectedSearchAction || defaultSearchAction;
  }
);

export const getLoadingReturnId = (state) => state.tools.returnsSearch.loadingReturnId;

export const getLineItemsOfSelectedReturn = createSelector([getReturnsSearchProperties], (returnsSearch) => {
  const { selectedReturn } = returnsSearch;
  if (!selectedReturn) {
    return [];
  }

  return selectedReturn.returnItems;
});

export const getReturnSearchQuery = createSelector(
  [getReturnsSearchProperties],
  (returnsSearch) => returnsSearch.selectedSearchQuery
);

export const getCustomers = (state) => state.tools.customerSearch.customers;
export const getSelectedCustomer = (state) => state.tools.customerSearch.selectedCustomer;
export const getAccounts = (state) => state.tools.accountSearch.accounts;
export const getSelectedAccount = (state) => state.tools.accountSearch.selectedAccount;
export const getSelectedPurchase = (state) => state.tools.accountSearch.selectedPurchase;

export const getECommerceSearchEmail = createSelector(
  [getOrderSearchQuery, getSelectedOrderSearchAction],
  (query, action) => {
    if (orderSearchOptions.EMAIL_SEARCH_TYPES.includes(action)) {
      return query;
    }

    return null;
  }
);

export const getSelectedProduct = createSelector(
  [getOrdersSearchProperties],
  (ordersSearch) => ordersSearch.selectedProduct
);

export const getProductsList = createSelector([getOrdersSearchProperties], (ordersSearch) => ordersSearch.products);

export const getStoreSearchProperties = (state) => state.tools.rbi.rbiStoreLocations || {};

export const getSelectedRbiStore = (state) => state.tools.rbi.selectedStore;

export const getRBIStoreLocationQuery = (state) => state.tools.rbi.storeLocationQuery;

export const getOrderTags = createSelector([getOrdersSearchProperties], (ordersSearch) => ordersSearch.orderTags);

// TODO this is a duplicate selector needed to avoid a
// circular dependency - attempt to remove
export const getSelectedOrderFromOrdersSearch = (state) => {
  return state.tools.ordersSearch.selectedOrder || {};
};

export const getShopNameOfSelectedOrder = createSelector(
  [getSelectedOrderFromOrdersSearch, getOrderingSystem],
  (selectedOrder, orderingSystem) => {
    switch (orderingSystem) {
      case 'shopify':
        return selectedOrder?.shopName;
      case 'common':
      default:
        return selectedOrder?.simplrParams?.domain;
    }
  }
);

export const getCustomerIdFromOrder = createSelector(
  [getSelectedOrderFromOrdersSearch, getOrderingSystem],
  (selectedOrder, orderingSystem) => {
    switch (orderingSystem) {
      case 'shopify':
        return selectedOrder?.customer?.id;
      case 'common':
      default:
        return selectedOrder?.customer?.customerId;
    }
  }
);

export const getPauseSubscriptionDetails = (state) => state.tools.subscriptionSearch.pauseSubscriptionDetails;

export const getDynamicOrderSearchValues = createSelector(
  [getOrdersSearchProperties],
  (ordersSearch) => ordersSearch.dynamicOrderSearchValues
);
